import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/government', label: 'Gov Contracting' },
  { to: '/contact', label: 'Contact' },
];

const SCROLL_THRESHOLD = 20;

function NavLink({ to, label, isActive }) {
  return (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`text-sm tracking-wide uppercase transition-colors duration-200 ${
        isActive ? 'text-[#F2B705]' : 'text-[#A3A3A3] hover:text-white'
      }`}
    >
      {label}
    </Link>
  );
}

function MobileNavLink({ to, label, isActive }) {
  return (
    <Link
      to={to}
      data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`text-sm tracking-wide uppercase py-2 ${
        isActive ? 'text-[#F2B705]' : 'text-[#A3A3A3]'
      }`}
    >
      {label}
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > SCROLL_THRESHOLD);
  }, [setScrolled]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#121212]/95 backdrop-blur-xl border-b border-white/10' : 'bg-[#121212]/70 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between" style={{ minHeight: '100px' }}>
          {/* Logo */}
          <Link to="/" data-testid="logo-link" style={{ width: '400px', overflow: 'visible', display: 'flex', alignItems: 'center' }}>
            <img 
              src="/logo-cropped.png"
              alt="Score Integrated Solutions LLC" 
              style={{ width: '380px', height: 'auto', maxHeight: '90px', objectFit: 'contain', objectPosition: 'left center', display: 'block' }}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <NavLink key={link.to} to={link.to} label={link.label} isActive={location.pathname === link.to} />
            ))}
            <Link
              to="/careers"
              data-testid="nav-cta-btn"
              className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-6 py-2.5 hover:bg-[#D9A404] transition-colors"
            >
              Careers
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#121212]/98 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <MobileNavLink key={link.to} to={link.to} label={link.label} isActive={location.pathname === link.to} />
            ))}
            <Link
              to="/careers"
              className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-6 py-3 text-center mt-2 hover:bg-[#D9A404] transition-colors"
            >
              Careers
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
